/**
 * Les icônes du site, en SVG inline.
 *
 * La maquette n'en livrait aucune : le HTML posait des formes CSS vides à leur
 * place (un carré bleu plein pour les cartes « Focus », un carré / un rond / une
 * gélule en bordure de 2px pour les assurances du hero). À l'écran ça se lit
 * comme des icônes qui n'ont pas chargé.
 *
 * Inline plutôt qu'une dépendance : le site est rendu en SSR, un `<svg>` dans le
 * HTML arrive peint dès la première frame — pas de second aller-retour réseau,
 * pas de saut de mise en page, et rien à charger avant que la page soit lisible.
 *
 * Tracés au format lucide (24×24, `stroke` à 2, bouts et jointures arrondis) :
 * `currentColor` partout, donc la couleur vient de la CSS du bloc qui les pose.
 */

import type { ReactNode } from 'react';

export type IconName =
  | 'lock'
  | 'shield-check'
  | 'users'
  | 'rocket'
  | 'trophy'
  | 'user-plus'
  | 'sparkles'
  | 'check'
  | 'message-circle'
  | 'rotate-ccw'
  | 'calendar-x'
  | 'eye'
  | 'clock';

const PATHS: Record<IconName, ReactNode> = {
  lock: (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  'shield-check': (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  rocket: (
    <>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </>
  ),
  trophy: (
    <>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
    </>
  ),
  'user-plus': (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6" />
      <path d="M22 11h-6" />
    </>
  ),
  sparkles: (
    <>
      <path d="M11.5 2.4a.5.5 0 0 1 1 0L14 8.5a2 2 0 0 0 1.44 1.44l6.1 1.56a.5.5 0 0 1 0 1L15.44 14a2 2 0 0 0-1.44 1.44l-1.5 6.1a.5.5 0 0 1-1 0L10 15.44A2 2 0 0 0 8.56 14l-6.1-1.5a.5.5 0 0 1 0-1L8.56 10A2 2 0 0 0 10 8.5z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  'message-circle': <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  'rotate-ccw': (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  'calendar-x': (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
      <path d="m14 14-4 4" />
      <path d="m10 14 4 4" />
    </>
  ),
  eye: (
    <>
      <path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
};

interface IconProps {
  name: IconName;
  className?: string;
}

/**
 * Toujours décoratif : chaque icône du site double un libellé déjà écrit à côté
 * d'elle, donc on la retire de l'arbre d'accessibilité au lieu de faire répéter
 * « Recrutement » deux fois à un lecteur d'écran.
 *
 * `width`/`height` sont posés en attributs et pas seulement en CSS : en dev,
 * Vite injecte la feuille de style après l'hydratation, et un `<svg>` sans
 * dimension propre remplit tout son parent le temps de cette frame. L'attribut
 * le plafonne à 24px ; la CSS du bloc appelant le redescend ensuite à 20.
 */
export function Icon({ name, className }: IconProps) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
